import type { pisti } from "@app/rules";
import type { CSSProperties } from "react";
import { useT } from "../i18n/context";
import { Card } from "./Card";
import { JACK_RANK, type Slots, slotOf, TOWARD } from "./pistiPileParts";
import type { PileSweep } from "./usePileSweep";

type Size = { w: number; h: number };
type Names = readonly (string | null)[];
type Shown = NonNullable<pisti.PistiView["pileTop"]>;

/**
 * A capture, said out loud (owner, 20 Sept 2026: "who took the cards and what happened cannot be
 * made out"): who took the pile and how many cards, why (the same rank, or a jack), "Pişti!" when
 * it is one. It hangs above the pile, so the cards below never move; the three face-down cards of
 * a first capture are shown beside them (`Stack`).
 */
export function CaptureNote({ view, names }: { view: pisti.PistiView; names: Names }) {
  const { t } = useT();
  const sweep = view.sweep;
  if (sweep === null) return null;
  const name = names[sweep.seat] ?? "?";
  const byJack = view.pileTop?.rank === JACK_RANK && sweep.matched.rank !== JACK_RANK;
  return (
    <div role="status" className="flex flex-col items-center gap-1">
      {sweep.pisti > 0 ? (
        <span className="text-legible animate-pop whitespace-nowrap text-[18px] font-extrabold text-gold">
          {t("pisti.banner", { name, points: sweep.pisti })}
        </span>
      ) : (
        <span className="text-legible animate-pop whitespace-nowrap rounded-pill border border-gold/70 bg-panel-strong px-3 py-1 text-[13px] font-extrabold">
          {t("pisti.took", { name, cards: sweep.taken })}
        </span>
      )}
      {/* Wraps: on a phone one line of it ran off both sides of the table (20 Sept 2026). */}
      <span className="text-legible max-w-[17em] text-balance text-center text-[11px] font-semibold text-ink/80">
        {t(sweep.first ? "pisti.why.first" : byJack ? "pisti.why.jack" : "pisti.why.rank")}
      </span>
    </div>
  );
}

/** A card of the row, tilted a little by its place in it; the last one took the pile. */
function RowCard({
  shown,
  card,
  tilt,
  first,
  ring,
}: {
  shown: Shown;
  card: Size;
  tilt: number;
  first: boolean;
  ring?: boolean;
}) {
  return (
    <div
      className={ring ? "rounded-[8px] ring-2 ring-gold" : ""}
      style={{ marginInlineStart: first ? 0 : -card.w * 0.46, rotate: `${tilt}deg` }}
    >
      <Card card={shown} width={card.w} height={card.h} state="plain" />
    </div>
  );
}

/**
 * The first capture of a deck, laid out at full size instead of stacked (owner, 20 Sept 2026: lay
 * the cards out side by side rather than shrinking them): the three that lay face down, for the
 * team that may look at them (Pagat, decision 213), then the card that was matched and the one
 * that took it. Absolutely placed, so the pile's own spot on the table never moves.
 */
export function FirstCaptureRow({
  hidden,
  matched,
  taker,
  card,
}: {
  hidden: readonly Shown[];
  matched: Shown;
  taker: Shown;
  card: Size;
}) {
  const { t } = useT();
  const key = (shown: Shown) => `${shown.suit}:${shown.rank}`;
  return (
    <div className="absolute top-0 left-1/2 flex -translate-x-1/2 animate-pop">
      <fieldset className="contents border-0 p-0" aria-label={t("pisti.faceDown")}>
        {hidden.map((shown, i) => (
          <RowCard key={key(shown)} shown={shown} card={card} tilt={i * 3 - 6} first={i === 0} />
        ))}
      </fieldset>
      <RowCard key={key(matched)} shown={matched} card={card} tilt={3} first={false} />
      <RowCard key={key(taker)} shown={taker} card={card} tilt={6} first={false} ring />
    </div>
  );
}

/** Card edges under the flying card: a pile goes, not one card. */
const BUNDLE = [3, 2, 1];

/** The taken pile on its way to whoever took it. */
export function TakenFlight({
  sweep,
  card,
  slots,
}: {
  sweep: PileSweep | null;
  card: Size;
  slots: Slots;
}) {
  if (sweep === null || sweep.stage !== "flying") return null;
  const going = TOWARD[slotOf(slots, sweep.seat) ?? "seatTop"];
  const k = card.w / 72;
  // From the middle of the table all the way to the taker's seat, like a won trick (owner, 20 Sept).
  const vars = {
    "--tx": `${going.dx * k * 2.6}px`,
    "--ty": `${going.dy * k * 2.6}px`,
    width: card.w,
    height: card.h,
    left: `calc(50% - ${card.w / 2}px)`,
    top: `calc(50% - ${card.h / 2}px)`,
  } as CSSProperties;
  return (
    <div className="pointer-events-none absolute z-30 animate-sweep" style={vars}>
      {BUNDLE.map((depth) => (
        <div
          key={depth}
          className="absolute inset-0 rounded-[8px] border border-edge bg-ink/85 shadow-pill"
          style={{ translate: `${-depth * 2 * k}px ${-depth * 1.6 * k}px` }}
        />
      ))}
      <div className="absolute inset-0">
        <Card card={sweep.card} width={card.w} height={card.h} state="plain" />
      </div>
    </div>
  );
}
